import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Alert,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

const wards = [
  'Port Reitz','Kipevu','Airport','Changamwe','Chaani','Jomvu Kuu','Miritini','Mikindani.',
  'Mjambere','Junda','Bamburi','Mwakirunge','Mtopanga','Magogoni','Shanzu',
  'Frere Town','Ziwa la Ng\'ombe','Mkomani','Kongowea','Kadzandani',
  'Mtongwe','Shika Adabu','Bofu','Likoni','Timbwani',
  'Tudor','Mji wa Kale/Makadara','Tononoka','Shimanzi/Ganjoni','Majengo',
];

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [ward, setWard] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const res = await apiService.getCurrentUser();
        if (res.success) {
          const u = res.data;
          setFirstName(u.firstName || '');
          setLastName(u.lastName || '');
          setEmail(u.email || '');
          setWard(u.ward || '');
          setAvatarUrl(u.avatarUrl || '');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploadingAvatar(true);
    try {
      const res = await apiService.uploadFile(file);
      setAvatarUrl(res.data.fileUrl);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    setSaving(true);
    try {
      const res = await apiService.updateCurrentUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ward: ward || undefined,
        avatarUrl: avatarUrl || undefined,
      });
      if (res.success) {
        updateUser(res.data);
        setSuccess('Profile updated successfully');
      } else {
        setError((res as any)?.message || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            My Profile
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Avatar
                      src={avatarUrl || undefined}
                      sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                    >
                      {!avatarUrl && user && `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()}
                    </Avatar>
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={uploadingAvatar}
                    >
                      {uploadingAvatar ? 'Uploading Avatar...' : 'Change Avatar'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleAvatarChange}
                      />
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    fullWidth
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    fullWidth
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={email}
                    disabled
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Ward</InputLabel>
                    <Select
                      label="Ward"
                      value={ward || ''}
                      onChange={(e) => setWard(e.target.value as string)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {wards.map((w) => (
                        <MenuItem key={w} value={w}>
                          {w}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Role"
                    fullWidth
                    value={user?.role || ''}
                    disabled
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={saving}
                      fullWidth
                    >
                      {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ProfilePage;
